import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { RowSelectionState } from '@tanstack/react-table';

// Components
import TanStackGrid from './TanStackGrid';
import ErrorMessage from '../UI/ErrorMessage';
import { SourceNotInTabView } from '../../modules/search/searchGrid/components/SourceNotInTabView';
import ActiveFiltersBar, { ACTIVE_FILTERS_BAR_SLOT_ID } from '../UI/ActiveFiltersBar';
import Alert from '../../global/Alert/Alert';
import SaveFiltersDialog from './components/SaveFiltersDialog';

// Hooks
import { useGridState } from './hooks/useGridState';
import { useFilterManagement } from './hooks/useFilterManagement';
import { useDataFetching } from './hooks/useDataFetching';
import { clampSkip, usePaginationAndSorting } from './hooks/usePaginationAndSorting';
import { useColumnManagement } from './hooks/useColumnManagement';
import { useFilterSaving } from './hooks/useFilterSaving';
import { useSelectionHandling } from './hooks/useSelectionHandling';

// Context and Utils
import { userAppContext } from '../../context/Context';
import { initializeFiltersFromURL } from './utils/index';
import { isSourceEnabledForTab } from '../../modules/search/searchGrid/helpers/sourceListHelpers';

// Types and Constants
import { GridProps } from './types/index';
import { SHOW_SUMMARIZE, ERROR_MESSAGES } from './constants/index';
import { buildEnrichmentTableRowId, getLogicalEnrichmentIdFromTableRowId } from './utils/index';
import RestrictedAccess from '../Modal/RestrictedAccess';

const SHOW_HISTORIAL_SCORE = process.env.REACT_APP_SHOW_HISTORIAL_SCORE === 'true';

const Grid: React.FC<GridProps> = ({
  columns,
  columnVisibilityModel,
  onColumnVisibilityModelChange,
  columnOrder,
  setColumnOrder,
  columnPinning,
  setColumnPinning,
  view,
  source,
  tab,
  panelTab,
  searchItems,
  isEvidence,
  isMapping,
  mappingContent: mappingContentFromProps,
  setTotalResults,
  getFilterValueRef,
  onData,
  sourceWeights,
  enableEnrichmentSelection = false,
  getRowId,
  getRowIdForEnrichment,
  enrichmentGetSelectionRef,
  enrichmentRestoreSelection = null,
  onEnrichmentRestoreApplied,
  rowDetailColumnCatalog,
  onFiltersChange,
  showSummaryMatchBar = false,
  onOpenSummaryMatch,
  isSummaryMatchLoading = false,
  summaryMatchEncodedApiParams = null,
  summaryMatchSessionKey = null,
  summaryMatchActive = false,
  summaryMatchStats = null,
  summaryMatchPreserveFilters = false,
  onClearSummaryMatch,
  ...rest
}) => {
  const [enrichmentRowSelection, setEnrichmentRowSelection] = useState<RowSelectionState>({});
  const enrichmentSelectedRowsMapRef = useRef<Record<string, any>>({});
  const enrichmentSelectedIdsRef = useRef<string[]>([]);
  const rowDataRef = useRef<any[]>([]);
  const userContext = userAppContext();
  const {
    isAuthenticated,
    groups,
    restrictBrowseAll,
    restrictSources,
    restrictGridResultCount,
    mappingContent: mappingContentFromContext,
    user,
  } = userContext;
  const mappingContent =
    mappingContentFromProps !== undefined ? mappingContentFromProps : mappingContentFromContext;

  const initialPageSize = useMemo(
    () => (typeof restrictGridResultCount === 'number' ? restrictGridResultCount : 10),
    [restrictGridResultCount],
  );

  // Initialize state with custom hooks
  const initialFilters = useMemo(
    () => initializeFiltersFromURL(rest.defaultFilterValue),
    [rest.defaultFilterValue],
  );
  const gridState = useGridState(initialFilters, initialPageSize);

  // Extract state and refs from gridState
  const {
    rowData,
    setRowData,
    totalCount,
    setTotalCount,
    loading,
    setLoading,
    showPopup,
    setShowPopup,
    skip,
    setSkip,
    limit,
    setLimit,
    orderByParam,
    setOrderByParam,
    order,
    setOrder,
    showFiltersModal,
    setShowFiltersModal,
    showCopiedAlert,
    setShowCopiedAlert,
    pendingFiltersToSave,
    setPendingFiltersToSave,
    alertMessage,
    setAlertMessage,
    filtersLoadedFromURL,
    setFiltersLoadedFromURL,
    isSessionExpired,
    setIsSessionExpired,
    defaultFilterValue,
    setDefaultFilterValue,
    cacheRef,
    gridContainerRef,
    prevDataRef,
    fetchTimeoutRef,
    prevTabRef,
  } = gridState;

  useEffect(() => {
    if (!isMapping || !isEvidence || !mappingContent) return;

    const selectedReferenceType = (mappingContent as { selectedReferenceType?: 'PMID' | 'NCTID' | 'ALL' })
      .selectedReferenceType;
    const selectedPmYear = Number((mappingContent as { selectedPmYear?: number }).selectedPmYear);
    const selectedPolarity = (mappingContent as { selectedPolarity?: 'Positive' | 'Negative' })
      .selectedPolarity;

    if (
      selectedReferenceType !== 'PMID' &&
      selectedReferenceType !== 'NCTID' &&
      selectedReferenceType !== 'ALL' &&
      selectedPolarity !== 'Positive' &&
      selectedPolarity !== 'Negative' &&
      !Number.isFinite(selectedPmYear)
    ) {
      return;
    }

    setDefaultFilterValue(prev => {
      const next = [...prev];
      let changed = false;

      if (selectedReferenceType === 'PMID' || selectedReferenceType === 'NCTID') {
        const idx = next.findIndex(f => f.name === 'reference_type');
        if (idx >= 0) {
          const newVal = [selectedReferenceType];
          const cur = next[idx];
          if (
            cur.operator !== 'eq' ||
            cur.type !== 'select' ||
            JSON.stringify(cur.value) !== JSON.stringify(newVal)
          ) {
            next[idx] = { ...cur, operator: 'eq', type: 'select', value: newVal };
            changed = true;
          }
        }
      } else if (selectedReferenceType === 'ALL') {
        const idx = next.findIndex(f => f.name === 'reference_type');
        if (idx >= 0) {
          const cur = next[idx];
          if (cur.value !== '') {
            next[idx] = { ...cur, operator: 'eq', type: 'select', value: '' };
            changed = true;
          }
        }
      }

      if (selectedPolarity === 'Positive' || selectedPolarity === 'Negative') {
        const idx = next.findIndex(f => f.name === 'association');
        const newVal = [selectedPolarity];
        if (idx >= 0) {
          const cur = next[idx];
          if (
            cur.operator !== 'eq' ||
            cur.type !== 'select' ||
            JSON.stringify(cur.value) !== JSON.stringify(newVal)
          ) {
            next[idx] = { ...cur, operator: 'eq', type: 'select', value: newVal };
            changed = true;
          }
        } else {
          next.push({
            name: 'association',
            operator: 'eq',
            type: 'select',
            value: newVal,
          });
          changed = true;
        }
      }

      if (Number.isFinite(selectedPmYear)) {
        const idx = next.findIndex(f => f.name === 'pmYear');
        if (idx >= 0) {
          const cur = next[idx];
          const newVal = String(selectedPmYear);
          if (cur.operator !== 'eq' || cur.type !== 'number' || cur.value !== newVal) {
            next[idx] = { ...cur, operator: 'eq', type: 'number', integer: true, value: newVal };
            changed = true;
          }
        }
      }

      return changed ? next : prev;
    });
  }, [isMapping, isEvidence, mappingContent, setDefaultFilterValue]);

  useEffect(() => {
    if (!SHOW_HISTORIAL_SCORE) return;
    const onOpenScoreHistoryFromActions = () => {
      window.dispatchEvent(new CustomEvent('disgenet:focus-bottom-detail-historical-tab'));
    };
    window.addEventListener('disgenet:open-score-history-from-actions', onOpenScoreHistoryFromActions);
    return () =>
      window.removeEventListener('disgenet:open-score-history-from-actions', onOpenScoreHistoryFromActions);
  }, []);

  rowDataRef.current = rowData ?? [];

  const [summaryMatchFetchError, setSummaryMatchFetchError] = useState<string | null>(null);

  const activeSummaryMatchEncodedParams =
    !isEvidence && summaryMatchActive && summaryMatchEncodedApiParams ? summaryMatchEncodedApiParams : null;

  const summaryMatchKey = summaryMatchActive && summaryMatchSessionKey ? summaryMatchSessionKey : null;
  const summaryMatchGeneCount = summaryMatchStats?.matchFilterCount ?? 0;

  useEffect(() => {
    if (summaryMatchActive && !isEvidence) {
      setSkip(0);
    } else {
      setSummaryMatchFetchError(null);
    }
  }, [summaryMatchActive, isEvidence, setSkip]);

  useEffect(() => {
    const nextSkip = clampSkip(skip, totalCount, limit);
    if (nextSkip !== skip) setSkip(nextSkip);
  }, [skip, totalCount, limit, setSkip]);

  // Initialize hooks
  const filterManagement = useFilterManagement(
    defaultFilterValue,
    setDefaultFilterValue,
    isEvidence,
    filtersLoadedFromURL,
    setFiltersLoadedFromURL,
    gridContainerRef,
    gridState.setURLWithFilters,
    setPendingFiltersToSave,
    setSkip,
  );

  const prevSummaryMatchActiveRef = useRef(false);
  useEffect(() => {
    const active = summaryMatchActive && !isEvidence;
    if (active && !prevSummaryMatchActiveRef.current) {
      if (!summaryMatchPreserveFilters) {
        filterManagement.handleClearAllFilters();
      }
      setSkip(0);
    }
    prevSummaryMatchActiveRef.current = active;
  }, [
    summaryMatchActive,
    summaryMatchPreserveFilters,
    isEvidence,
    filterManagement.handleClearAllFilters,
    setSkip,
  ]);

  useDataFetching({
    view,
    source,
    tab,
    searchItems,
    isEvidence,
    isMapping,
    skip,
    limit,
    orderByParam,
    order,
    defaultFilterValue,
    sourceWeights,
    userContext,
    mappingContent,
    checkIncompleteFilters: filterManagement.checkIncompleteFilters,
    setRowData,
    setTotalCount,
    setTotalResults,
    setLoading,
    setIsSessionExpired,
    setShowPopup,
    setIncompleteFilterMessage: () => {},
    cacheRef,
    prevDataRef,
    fetchTimeoutRef,
    summaryMatchEncodedParams: activeSummaryMatchEncodedParams,
    summaryMatchKey,
    summaryMatchGeneCount,
    onSummaryMatchError: setSummaryMatchFetchError,
  });

  const isTrial = groups?.includes('ROLE_TRIAL');

  const handleClosePopup = () => {
    setShowPopup(false);
    setSkip(0);
  };

  const paginationAndSorting = usePaginationAndSorting({
    isAuthenticated,
    isTrial,
    restrictBrowseAll,
    limit,
    orderByParam,
    order,
    setSkip,
    setLimit,
    setOrderByParam,
    setOrder,
  });

  const columnManagement = useColumnManagement({ columns, tab });

  const filterSaving = useFilterSaving({
    pendingFiltersToSave,
    isEvidence,
    tab,
    view,
    source,
    searchItems,
    user,
    setAlertMessage,
    setShowCopiedAlert,
    setShowFiltersModal,
  });

  const selectionHandling = useSelectionHandling({ onData, setSelectedRow: () => {} });

  const onDataRef = useRef(onData);
  onDataRef.current = onData;

  const getRowIdForEnrichmentRef = useRef(getRowIdForEnrichment);
  getRowIdForEnrichmentRef.current = getRowIdForEnrichment;

  const skipRef = useRef(skip);
  skipRef.current = skip;

  const enrichmentTableGetRowId = useCallback(
    (row: any, index: number) => {
      const logical =
        getRowIdForEnrichmentRef.current?.(row)?.trim() || getRowId?.(row, index)?.trim() || 'row';
      return buildEnrichmentTableRowId(logical, skipRef.current, index);
    },
    [getRowId],
  );

  const resolveRowForEnrichmentId = useCallback(
    (id: string, data: any[] | undefined, prev: Record<string, any>) => {
      if (prev[id] !== undefined) return prev[id];
      const byExact = data?.find((r: any, i: number) => enrichmentTableGetRowId(r, i) === id);
      if (byExact !== undefined) return byExact;
      const logicalId = getLogicalEnrichmentIdFromTableRowId(id);
      if (!logicalId || logicalId === 'row') return undefined;
      return data?.find((r: any) => getRowIdForEnrichmentRef.current?.(r) === logicalId);
    },
    [enrichmentTableGetRowId],
  );

  const syncEnrichmentSelectionSnapshot = useCallback(
    (selection: RowSelectionState, data: any[] | undefined) => {
      const selectedTableIds = Object.keys(selection).filter(k => selection[k]);
      enrichmentSelectedIdsRef.current = selectedTableIds;
      const prevMap = enrichmentSelectedRowsMapRef.current;
      const nextMap: Record<string, any> = {};
      for (const id of selectedTableIds) {
        const row = resolveRowForEnrichmentId(id, data, prevMap);
        if (row !== undefined) nextMap[id] = row;
      }
      enrichmentSelectedRowsMapRef.current = nextMap;
      const selectedRows = selectedTableIds.map((id: string) => nextMap[id]).filter(Boolean);
      // Table keys contain the exact exportable identifier. They are the source of truth
      // for the counter because rowData can be transiently empty/partial while paginating.
      const logicalIds = [
        ...new Set(
          selectedTableIds
            .map(getLogicalEnrichmentIdFromTableRowId)
            .filter(id => id.length > 0 && id !== 'row'),
        ),
      ];
      return {
        selectedTableIds,
        selectedRows,
        uniqueCount: logicalIds.length,
        logicalIds,
      };
    },
    [resolveRowForEnrichmentId],
  );

  const handleEnrichmentRowSelectionChange = useCallback(
    (updater: any) => {
      setEnrichmentRowSelection(prev => {
        const next = typeof updater === 'function' ? updater(prev) : updater;
        const { selectedRows, uniqueCount, logicalIds } = syncEnrichmentSelectionSnapshot(
          next,
          rowDataRef.current,
        );
        const cb = onDataRef.current;
        if (cb) {
          queueMicrotask(() => cb(selectedRows.length > 0, selectedRows, uniqueCount, logicalIds));
        }
        return next;
      });
    },
    [syncEnrichmentSelectionSnapshot],
  );

  useEffect(() => {
    if (!enrichmentGetSelectionRef) return;
    enrichmentGetSelectionRef.current = () => {
      const selectedTableIds = enrichmentSelectedIdsRef.current;
      const selectedRows = selectedTableIds
        .map((id: string) => enrichmentSelectedRowsMapRef.current[id])
        .filter(Boolean);
      const fromKeys = [
        ...new Set(
          selectedTableIds
            .map(getLogicalEnrichmentIdFromTableRowId)
            .filter(id => id.length > 0 && id !== 'row'),
        ),
      ];
      return {
        selectedIds: fromKeys,
        selectedTableIds,
        selectedRows,
        selectedRowsById: { ...enrichmentSelectedRowsMapRef.current },
        rowData: rowDataRef.current ?? [],
        getRowId: enableEnrichmentSelection ? enrichmentTableGetRowId : (getRowId ?? null),
        tab: panelTab ?? tab,
      };
    };
  }, [
    enrichmentGetSelectionRef,
    getRowId,
    tab,
    panelTab,
    enableEnrichmentSelection,
    enrichmentTableGetRowId,
    enrichmentRowSelection,
  ]);

  useEffect(() => {
    if (!enableEnrichmentSelection) return;
    const { selectedRows, uniqueCount, logicalIds } = syncEnrichmentSelectionSnapshot(
      enrichmentRowSelection,
      rowDataRef.current,
    );
    const cb = onDataRef.current;
    if (cb) cb(selectedRows.length > 0, selectedRows, uniqueCount, logicalIds);
  }, [enableEnrichmentSelection, enrichmentRowSelection, rowData, syncEnrichmentSelectionSnapshot, skip]);

  useEffect(() => {
    if (!enableEnrichmentSelection) {
      setEnrichmentRowSelection({});
      enrichmentSelectedRowsMapRef.current = {};
      enrichmentSelectedIdsRef.current = [];
    }
  }, [enableEnrichmentSelection]);

  useEffect(() => {
    if (!enableEnrichmentSelection || !enrichmentRestoreSelection) return;
    const { selectedTableIds, selectedRowsById } = enrichmentRestoreSelection;
    const next: RowSelectionState = {};
    for (const id of selectedTableIds) {
      next[id] = true;
    }
    enrichmentSelectedRowsMapRef.current = { ...(selectedRowsById || {}) };
    enrichmentSelectedIdsRef.current = selectedTableIds;
    setEnrichmentRowSelection(next);
    const { selectedRows, uniqueCount, logicalIds } = syncEnrichmentSelectionSnapshot(
      next,
      rowDataRef.current,
    );
    const cb = onDataRef.current;
    if (cb) cb(selectedRows.length > 0, selectedRows, uniqueCount, logicalIds);
    onEnrichmentRestoreApplied?.();
  }, [
    enableEnrichmentSelection,
    enrichmentRestoreSelection,
    onEnrichmentRestoreApplied,
    syncEnrichmentSelectionSnapshot,
  ]);

  const prevTabEnrichmentRef = useRef<string>(tab);
  useEffect(() => {
    if (prevTabEnrichmentRef.current !== tab) {
      prevTabEnrichmentRef.current = tab;
      setEnrichmentRowSelection({});
      enrichmentSelectedRowsMapRef.current = {};
      enrichmentSelectedIdsRef.current = [];
    }
  }, [tab]);

  const prevLimitEnrichmentRef = useRef(limit);
  useEffect(() => {
    if (prevLimitEnrichmentRef.current !== limit) {
      prevLimitEnrichmentRef.current = limit;
      // Row ids include skip/index; page-size changes invalidate previous keys.
      setEnrichmentRowSelection({});
      enrichmentSelectedRowsMapRef.current = {};
      enrichmentSelectedIdsRef.current = [];
    }
  }, [limit]);

  const prevIsEvidenceRef = useRef(isEvidence);
  useEffect(() => {
    if (prevIsEvidenceRef.current !== isEvidence) {
      setOrderByParam(undefined);
      setOrder(undefined);
      prevIsEvidenceRef.current = isEvidence;
    }
  }, [isEvidence, setOrderByParam, setOrder]);

  // Memoized values for TanStackGrid
  const memoizedFilterValues = useMemo(
    () =>
      defaultFilterValue.reduce((acc: Record<string, string>, filter) => {
        if (
          filter.value !== undefined &&
          filter.value !== null &&
          filter.value !== '' &&
          !(Array.isArray(filter.value) && filter.value.length === 0)
        ) {
          acc[filter.name] = Array.isArray(filter.value) ? filter.value.join(',') : filter.value;
        }
        return acc;
      }, {}),
    [defaultFilterValue],
  );

  const memoizedFilterOperators = useMemo(
    () =>
      defaultFilterValue.reduce((acc: Record<string, string>, filter) => {
        if (filter.operator) {
          acc[filter.name] = filter.operator;
        }
        return acc;
      }, {}),
    [defaultFilterValue],
  );

  const memoizedFilterEmptiness = useMemo(
    () =>
      defaultFilterValue.reduce((acc: Record<string, string>, filter) => {
        if (filter.emptiness) {
          acc[filter.name] = filter.emptiness;
        }
        return acc;
      }, {}),
    [defaultFilterValue],
  );

  useEffect(() => {
    if (prevTabRef.current !== tab) {
      const urlParams = new URLSearchParams(window.location.search);
      const hasLegacyFilterInUrl = urlParams.get('f');
      const hasModernFilterInUrl = [...urlParams.keys()].some(
        k => k.startsWith('filters-') || k.startsWith('filters['),
      );
      const isLoadingFromSavedSearch =
        (hasLegacyFilterInUrl || hasModernFilterInUrl) && !filtersLoadedFromURL;
      if (!isLoadingFromSavedSearch) {
        filterManagement.handleClearAllFilters();
      }
      setFiltersLoadedFromURL(false);
      prevTabRef.current = tab;
    }
  }, [tab, filterManagement, setFiltersLoadedFromURL, filtersLoadedFromURL]);

  // Set filter ref for external access
  if (getFilterValueRef) {
    getFilterValueRef.current = () => defaultFilterValue;
  }

  useEffect(() => {
    if (onFiltersChange) {
      onFiltersChange(defaultFilterValue);
    }
  }, [defaultFilterValue, onFiltersChange]);

  // Validation checks
  const isInvalidSource = source && !isSourceEnabledForTab(source, tab, restrictSources);

  if (isInvalidSource) {
    return <SourceNotInTabView source={source} tab={tab} view={view} />;
  }

  // Render no columns error
  if (!columns || columns.length === 0) {
    return (
      <ErrorMessage
        title={ERROR_MESSAGES.NO_COLUMNS}
        subtitle={<span>Please select columns to display in the grid.</span>}
        style={{ minHeight: 537 }}
      />
    );
  }

  // Render session expired error
  if (isSessionExpired) {
    return <ErrorMessage title={ERROR_MESSAGES.SESSION_EXPIRED} subtitle={<p>Please refresh the page</p>} />;
  }

  return (
    <>
      <RestrictedAccess isRestricted={!isAuthenticated && !searchItems.length}>
        {showCopiedAlert && <Alert message={alertMessage} type="success" />}

        <ActiveFiltersBar
          filters={defaultFilterValue}
          onRemoveFilter={filterManagement.handleRemoveFilter}
          onRemoveFilterValue={filterManagement.handleRemoveFilterValue}
          onClearAll={filterManagement.handleClearAllFilters}
          isEvidence={isEvidence}
          showSummaryMatchBar={showSummaryMatchBar}
          onOpenSummaryMatch={onOpenSummaryMatch}
          isSummaryMatchLoading={isSummaryMatchLoading}
          summaryMatchActive={summaryMatchActive}
          summaryMatchStats={summaryMatchStats}
          summaryMatchError={summaryMatchFetchError}
          onClearSummaryMatch={onClearSummaryMatch}
          portalTargetId={!isMapping ? ACTIVE_FILTERS_BAR_SLOT_ID : undefined}
        />

        <TanStackGrid
          key={`${panelTab ?? tab}-${tab}-${view}-${source}-${isEvidence ? 'evidence' : 'summary'}${summaryMatchActive ? '-match' : ''}${enableEnrichmentSelection ? `-enrichment-${skip}-${limit}` : ''}`}
          columns={columnManagement.memoizedColumns}
          data={rowData}
          loading={loading}
          pagination={true}
          manualPagination={true}
          totalCount={totalCount}
          pageIndex={Math.floor(skip / limit)}
          pageSize={limit}
          pageSizeOptions={paginationAndSorting.memoizedPageSizes}
          onPageChange={paginationAndSorting.handlePageChange}
          onPageSizeChange={paginationAndSorting.handlePageSizeChange}
          manualSorting={true}
          sorting={paginationAndSorting.memoizedSorting}
          onSortingChange={paginationAndSorting.handleSortingChange}
          enableRowSelection={(isEvidence && SHOW_SUMMARIZE) || enableEnrichmentSelection}
          getRowId={enableEnrichmentSelection ? enrichmentTableGetRowId : undefined}
          rowSelection={enableEnrichmentSelection ? enrichmentRowSelection : undefined}
          onRowSelectionChange={
            enableEnrichmentSelection
              ? handleEnrichmentRowSelectionChange
              : selectionHandling.onSelectionChange
          }
          showFilters={true}
          filterValues={memoizedFilterValues}
          filterOperators={memoizedFilterOperators}
          onFilterChange={filterManagement.handleFilterChange}
          onFilterOperatorChange={filterManagement.handleFilterOperatorChange}
          onBatchFilterChange={filterManagement.handleFilterBatch}
          filterEmptiness={memoizedFilterEmptiness}
          onFilterEmptinessChange={filterManagement.handleFilterEmptinessChange}
          columnOrder={columnOrder}
          onColumnOrderChange={setColumnOrder}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={onColumnVisibilityModelChange}
          columnPinning={columnPinning}
          onColumnPinningChange={setColumnPinning}
          isAuthenticated={isAuthenticated}
          searchItems={searchItems}
          showPopup={showPopup}
          onClosePopup={handleClosePopup}
          rowDetailColumnCatalog={rowDetailColumnCatalog}
        />

        <SaveFiltersDialog
          isOpen={showFiltersModal}
          onClose={() => setShowFiltersModal(false)}
          onConfirm={filterSaving.handleSaveFilters}
          tab={tab}
          view={view}
          source={source}
          searchItems={searchItems}
          pendingFiltersToSave={pendingFiltersToSave}
        />
      </RestrictedAccess>
    </>
  );
};

export default Grid;
